import { useMemo } from "react"

import {
  COUNTRIES,
  type CountryCode,
  type ISOCountryCode,
} from "@repo/shared/countries"

import { OptionPicker } from "./option-picker"

export type { CountryCode, ISOCountryCode }

export interface CountrySelectProps {
  readonly value: CountryCode | undefined
  readonly onChange: (value: CountryCode) => void
  /** Filter to only show these country codes. Use override codes (e.g., "UK" not "GB"). */
  readonly countries?: readonly CountryCode[]
  readonly placeholder?: string
  readonly searchPlaceholder?: string
  readonly className?: string
  readonly disabled?: boolean
}

/**
 * The country picker — the shared `OptionPicker` (searchable, virtualized,
 * theme-tokened) over the country list. One picker implementation serves
 * every option-backed control; the bespoke `VirtualizedCombobox` this used to
 * ride is retired (#155: its menu owned the keyboard and its styling was
 * hardcoded white-glass).
 *
 * The list itself (and its code overrides, currently GB -> UK) lives in
 * `@repo/shared/countries` so field declarations and this picker read one
 * source.
 *
 * @example
 * // All countries
 * <CountrySelect value={country} onChange={setCountry} />
 *
 * @example
 * // Only specific countries (use "UK" not "GB")
 * <CountrySelect
 *   value={country}
 *   onChange={setCountry}
 *   countries={["UK", "US", "CA"]}
 * />
 */
export function CountrySelect({
  value,
  onChange,
  countries,
  placeholder = "Select country",
  searchPlaceholder = "Search countries...",
  className,
  disabled,
}: CountrySelectProps) {
  const options = useMemo(() => {
    const allowed =
      countries && countries.length > 0
        ? new Set(countries as readonly string[])
        : null
    return COUNTRIES.filter((c) => !allowed || allowed.has(c.code)).map(
      (c) => ({
        value: c.code,
        label: c.name,
      }),
    )
  }, [countries])

  return (
    <OptionPicker
      value={value}
      onValueChange={(v) => {
        if (v) onChange(v as CountryCode)
      }}
      options={options}
      placeholder={placeholder}
      // A country list is a picker whatever its size — never toggle buttons.
      forceMode="select"
      searchable
      searchPlaceholder={searchPlaceholder}
      className={className}
      disabled={disabled}
    />
  )
}
